import React, { useEffect, useMemo, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import PrestamosParametrizadosService from "../../services/prestamosParametrizadosService";
import { getApiError } from "./utils";

/**
 * Modal de SOLO LECTURA con los clientes asignados a un préstamo por clientes.
 * No permite agregar ni quitar: para modificar la lista hay que usar "Editar".
 *
 * Props:
 *   prestamo — { id, titulo, cantidadClientes }
 *   onClose  — cierra el modal
 */
export default function ClientesVerModal({ prestamo, onClose }) {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await PrestamosParametrizadosService.getById(prestamo.id);
        const lista = res?.data?.data?.clientes;
        if (!cancelled) setClientes(Array.isArray(lista) ? lista : []);
      } catch (err) {
        if (!cancelled)
          setError(getApiError(err, "No se pudieron cargar los clientes del préstamo.").message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prestamo.id]);

  const visibles = useMemo(() => {
    const f = filtro.trim().toLowerCase();
    const lista = [...clientes].sort((a, b) =>
      String(a.nombre ?? "").localeCompare(String(b.nombre ?? "")),
    );
    if (!f) return lista;
    return lista.filter(
      (c) =>
        String(c.documento ?? "").includes(f) ||
        String(c.idCliente).includes(f) ||
        String(c.nombre ?? "").toLowerCase().includes(f),
    );
  }, [clientes, filtro]);

  const deBaja = clientes.filter((c) => c.deBaja).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">Clientes del préstamo</h2>
            <p className="text-sm text-gray-500">{prestamo.titulo}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Cerrar"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 gap-3">
          <p className="rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-800">
            Vista de solo lectura. Para agregar o quitar clientes usá <b>Editar</b> en la tabla.
          </p>

          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="h-7 w-7 rounded-full border-4 border-custom-green-ccard-1 border-t-transparent animate-spin" />
              <span className="ml-3 text-sm text-gray-500">Cargando clientes…</span>
            </div>
          )}

          {!loading && error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {!loading && !error && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-gray-600">
                  <b>{clientes.length}</b> cliente{clientes.length !== 1 ? "s" : ""} asignado
                  {clientes.length !== 1 ? "s" : ""}
                  {deBaja > 0 && (
                    <span className="ml-2 text-red-600">
                      ({deBaja} con la cuenta dada de baja: no ven el préstamo)
                    </span>
                  )}
                </span>
                <input
                  className="w-56 rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-custom-green-ccard-1"
                  placeholder="Buscar DNI, ID o nombre"
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                />
              </div>

              <div className="flex-1 overflow-y-auto rounded-xl border border-gray-200">
                {visibles.length === 0 ? (
                  <p className="px-3 py-6 text-center text-xs text-gray-400">
                    {clientes.length === 0
                      ? "El préstamo no tiene clientes asignados."
                      : "Ningún cliente coincide con la búsqueda."}
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 text-gray-500">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">DNI</th>
                        <th className="px-3 py-2 text-left font-medium">Cliente</th>
                        <th className="px-3 py-2 text-left font-medium">ID cliente</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {visibles.map((c) => (
                        <tr key={c.idCliente} className="hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-gray-700">{c.documento ?? "—"}</td>
                          <td className="px-3 py-1.5 text-gray-600">
                            {c.nombre ?? "—"}
                            {c.deBaja && (
                              <span className="ml-1 rounded bg-red-50 px-1 text-[10px] text-red-600">
                                de baja
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-gray-400">{c.idCliente}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {filtro && (
                <p className="text-right text-[11px] text-gray-400">
                  {visibles.length} de {clientes.length}
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
